import type { AssistantChatMessage as AssistantChatMessageModel } from '@public-api-contracts/models/chat/ChatMessage';
import './AssistantChatMessage.less';
import MarkdownText from '../utils/MarkdownText';
import useAppDispatch from '../../hooks/useAppDispatch';

import AssistantChatMessageFeature from './AssistantChatMessageFeature';
import type { IconId } from '../icon/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { getProductVariantDescription } from '../../services/ProductService';
import ProductAddedToCartMessage from './ProductAddedToCartMessage';
import AssistantChatMessageButton from './AssistantChatMessageButton';

export type AssistantChatMessageProps = {
    assistantChatMessage: AssistantChatMessageModel;
};

const AssistantChatMessage = ({
    assistantChatMessage
}: AssistantChatMessageProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { content } = assistantChatMessage;

    const getProducts = () => {
        if (content.contentType !== 'feature') {
            return;
        }
        switch (content.feature.featureType) {
            case 'product-search-similar':
                return content.feature.result?.similarToProducts;
            case 'product-search-combinations':
            case 'product-search-complementary-category':
            case 'product-mix-and-match':
                return content.feature.result?.combinationWithProducts;
            case 'product-multi-variant-add-to-cart':
                return content.feature.result ?? [];
            default:
                return [];
        }
    };

    const featureClickHandler = () => {
        if (content.contentType !== 'feature') {
            return;
        }
        dispatch.featureState.restoreFeatureItem(content.feature);
    };

    const getFeatureIconId = (): IconId => {
        if (content.contentType !== 'feature') {
            return 'star';
        }
        switch (content.feature.featureType) {
            case 'product-search-by-text':
                return 'search-magic';
            case 'product-search-similar':
                return 'similar';
            case 'product-search-combinations':
                return 'join';
            case 'product-search-complementary-category':
                return 'join';
            case 'product-search-similar-by-image':
                return 'similar';
            case 'product-search-combinations-by-image':
                return 'join';
            case 'product-mix-and-match':
                return 'outfit';
            case 'product-comparison':
                return 'compare';
            case 'product-multi-variant-add-to-cart':
                return 'cart-plus';
            default:
                return 'star';
        }
    };

    const getFeatureText = (): string => {
        if (content.contentType !== 'feature') {
            return '';
        }
        switch (content.feature.featureType) {
            case 'product-search-by-text':
                return content.feature.request.searchText;
            case 'product-search-similar':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_SIMILAR');
            case 'product-search-combinations':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_COMBINATIONS');
            case 'product-search-complementary-category':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_COMPLEMENTARY_CATEGORY', {
                    CATEGORY:
                        content.feature.request.categoryFilter
                            .categoryTranslatedValue ??
                        content.feature.request.categoryFilter.categoryValue
                });
            case 'product-search-new':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_NEW');
            case 'product-search-similar-by-image':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_SIMILAR_BY_IMAGE');
            case 'product-search-combinations-by-image':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_COMBINATIONS_BY_IMAGE');
            case 'product-mix-and-match':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_MIX_AND_MATCH');
            case 'product-comparison':
                return t('PRODUCT_SEARCH_CHAT_FEATURE_COMPARISON');
            case 'product-multi-variant-add-to-cart':
                return t(
                    'PRODUCT_SEARCH_CHAT_FEATURE_MULTI_VARIANT_ADD_TO_CART'
                );
            default:
                return '';
        }
    };

    const getQuantity = (): number | undefined => {
        if (content.contentType !== 'feature') {
            return;
        }
        switch (content.feature.featureType) {
            case 'product-search-by-text':
                return content.feature.result?.totalQuantity;
            default:
                return;
        }
    };

    switch (content.contentType) {
        case 'markdown':
            return (
                <div className={`bri-AssistantChatMessage bri-DisplayFont`}>
                    <MarkdownText text={content.markdown} />
                </div>
            );
        case 'button':
            return <AssistantChatMessageButton content={content} />;
        case 'apply-filters':
            return (
                <div className={`bri-AssistantChatMessage`}>
                    <MarkdownText
                        text={`Applied ${content.categoryFilters.length + content.fieldFilters.length} filters`}
                    />
                </div>
            );
        case 'add-to-cart': {
            const { product, variantId } = content;
            const variantsDescription = getProductVariantDescription(
                product,
                variantId
            );
            return (
                <div className="bri-AssistantChatMessage-stacked">
                    <ProductAddedToCartMessage product={product} />
                    <div className={`bri-AssistantChatMessage bri-DisplayFont`}>
                        <MarkdownText
                            text={t('PRODUCT_CARD_ADDED_RESULT', {
                                DESCRIPTION: variantsDescription
                            })}
                        />
                    </div>
                    <AssistantChatMessageButton
                        content={{
                            contentType: 'button',
                            featureType: 'product-search-combinations',
                            contextualProductIds: [product.productId]
                        }}
                    />
                    <AssistantChatMessageButton
                        content={{
                            contentType: 'button',
                            featureType: 'product-mix-and-match',
                            contextualProductIds: [product.productId]
                        }}
                    />
                </div>
            );
        }
        case 'feature': {
            return (
                <AssistantChatMessageFeature
                    className={`bri-AssistantChatMessageFeature`}
                    iconId={getFeatureIconId()}
                    text={getFeatureText()}
                    products={getProducts()}
                    quantity={getQuantity()}
                    onClick={featureClickHandler}
                />
            );
        }
        default:
            return null;
    }
};

export default AssistantChatMessage;
